import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { Stamp, Lock, Check, Compass, Share2, Link2, Trophy, BookCheck } from "lucide-react";
import { useCompletedTrips } from "@/lib/useDiary";
import { useTrophies } from "@/lib/useTrophies";
import { fetchCountries } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { usePassport, BADGES, badgeFor } from "@/lib/usePassport";
import { copyText, canNativeShare, nativeShare } from "@/lib/share";
import { trackEvent } from "@/lib/analytics";
import RewardedAd from "@/components/RewardedAd";

function Badge({ badge, unlocked, t }) {
  return (
    <li className={`rounded-2xl border p-3 flex items-center gap-3 ${unlocked ? "border-ochre bg-ochre/10" : "border-stoneLine bg-card opacity-70"}`} data-testid={`badge-${badge.id}`}>
      <span className={`w-10 h-10 rounded-full flex items-center justify-center ${unlocked ? "bg-ochre text-white" : "bg-sandDeep text-muted-foreground"}`}>{unlocked ? <Check className="w-5 h-5" /> : <Lock className="w-4 h-4" />}</span>
      <span className="min-w-0"><span className="block font-semibold text-sm text-ink">{t(`badge_${badge.id}`)}</span><span className="block text-xs text-muted-foreground">{t("badge_req").replace("{n}", badge.min)}</span></span>
    </li>
  );
}

export default function Passport() {
  const { lang, t } = useLang();
  const navigate = useNavigate();
  const { data: countries = [] } = useQuery({ queryKey: ["countries"], queryFn: fetchCountries });
  const { stamps, count } = usePassport();
  const completedTrips = useCompletedTrips();
  const tripsDone = Object.keys(completedTrips).length;
  const { data: trophies } = useTrophies();
  const wins = trophies?.mine?.length || 0;
  const total = countries.length;
  const current = badgeFor(count);
  const [copied, setCopied] = React.useState(false);
  const sorted = [...countries].sort((a, b) => (a.short_names[lang] || "").localeCompare(b.short_names[lang] || "", lang));
  const text = t("passport_share").replace("{n}", count).replace("{total}", total).replace("{badge}", current ? t(`badge_${current.id}`) : t("badge_none"));
  const url = `${window.location.origin}/?lang=${lang}`;
  const share = async () => {
    trackEvent("share", { method: "native", content_type: "passport", lang });
    if (canNativeShare()) await nativeShare({ title: text, text, url });
    else { await copyText(`${text}\n${url}`); setCopied(true); setTimeout(() => setCopied(false), 1800); }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-8 py-10" data-testid="passport-page">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-ochre"><Stamp className="w-3.5 h-3.5" /> {t("passport_kicker")}</div>
          <h1 className="mt-1 text-4xl sm:text-5xl font-display font-bold text-ink tracking-tight">{t("passport")}</h1>
          <p className="mt-2 text-muted-foreground">{t("passport_intro")}</p>
        </div>
        <div className="rounded-2xl bg-ink text-sand px-5 py-4 flex items-center gap-4">
          <span className="stat-number text-4xl" data-testid="passport-page-count">{count}<span className="text-sand/50 text-xl">/{total}</span></span>
          <span className="text-xs text-sand/80 leading-tight max-w-[10rem]">{current ? <>{t("passport_current")} <b className="text-ochre">{t(`badge_${current.id}`)}</b></> : t("passport_start")}</span>
        </div>
      </header>

      <ul className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3 mb-10" data-testid="passport-grid">
        {sorted.map((c) => {
          const done = !!stamps[c.code];
          return (
            <li key={c.code}>
              <button onClick={() => navigate(`/?c=${c.code}&lang=${lang}`)} data-testid={`stamp-${c.code}`}
                className={`w-full rounded-2xl border p-2.5 flex flex-col items-center gap-1.5 transition-colors ${done ? "border-ochre/60 bg-card hover:border-ochre" : "border-dashed border-stoneLine bg-sand/40 hover:bg-sandDeep/40"}`}>
                <span className="relative">
                  <img src={c.flag_url} alt="" className={`w-14 h-10 object-cover rounded-md border border-stoneLine ${done ? "" : "grayscale opacity-40"}`} />
                  {done && <span className="absolute -top-2 -end-2 w-6 h-6 rounded-full bg-ochre text-white flex items-center justify-center rotate-12 shadow"><Stamp className="w-3.5 h-3.5" /></span>}
                  {completedTrips[c.code] && <span className="absolute -bottom-2 -start-2 w-6 h-6 rounded-full bg-[#1F5A36] text-white flex items-center justify-center -rotate-12 shadow" data-testid={`diary-stamp-${c.code}`}><BookCheck className="w-3.5 h-3.5" /></span>}
                </span>
                <span className={`text-[11px] font-medium truncate w-full text-center ${done ? "text-ink" : "text-muted-foreground"}`}>{c.short_names[lang]}</span>
              </button>
            </li>
          );
        })}
      </ul>

      <h2 className="text-base md:text-lg font-display font-bold text-ink mb-3">{t("badges")}</h2>
      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8" data-testid="badge-list">
        {BADGES.map((b) => <Badge key={b.id} badge={b} unlocked={count >= b.min} t={t} />)}
        <li className={`rounded-2xl border p-3 flex items-center gap-3 ${tripsDone ? "border-[#1F5A36]/50 bg-[#D6EDDC]/40" : "border-stoneLine bg-card opacity-70"}`} data-testid="badge-diary" data-trips={tripsDone}>
          <span className={`w-10 h-10 rounded-full flex items-center justify-center ${tripsDone ? "bg-[#1F5A36] text-white" : "bg-sandDeep text-muted-foreground"}`}><BookCheck className="w-5 h-5" /></span>
          <span className="min-w-0 flex-1">
            <span className="block font-semibold text-sm text-ink">{t("badge_diary")}{tripsDone > 1 && <span className="ms-1 text-[#1F5A36]">×{tripsDone}</span>}</span>
            <span className="block text-xs text-muted-foreground">{tripsDone === 1 ? t("diary_badge_done_one") : tripsDone ? t("diary_badge_done").replace("{n}", tripsDone) : t("diary_badge_req")}</span>
          </span>
        </li>
        <li className={`rounded-2xl border p-3 flex items-center gap-3 sm:col-span-2 ${wins ? "border-amber-400 bg-amber-50" : "border-stoneLine bg-card opacity-70"}`} data-testid="badge-champion" data-wins={wins}>
          <span className={`w-10 h-10 rounded-full flex items-center justify-center ${wins ? "bg-amber-400 text-white" : "bg-sandDeep text-muted-foreground"}`}><Trophy className="w-5 h-5" /></span>
          <span className="min-w-0 flex-1">
            <span className="block font-semibold text-sm text-ink">{t("badge_champion")}{wins > 1 && <span className="ms-1 text-amber-600">×{wins}</span>}</span>
            <span className="block text-xs text-muted-foreground">{wins ? t("champion_won").replace("{total}", trophies.mine[0].total) : t("champion_req")}</span>
          </span>
        </li>
      </ul>

      <div className="flex flex-wrap gap-2">
        <Link to={`/?lang=${lang}`} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-ink text-sand text-sm font-medium hover:bg-ochre transition-colors" data-testid="passport-explore"><Compass className="w-4 h-4" /> {t("passport_explore")}</Link>
        <RewardedAd />
        <button onClick={share} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-stoneLine text-sm font-medium hover:bg-sandDeep/50" data-testid="passport-share">
          {copied ? <Check className="w-4 h-4 text-oasis" /> : canNativeShare() ? <Share2 className="w-4 h-4" /> : <Link2 className="w-4 h-4" />} {copied ? t("copied") : t("share")}
        </button>
      </div>
    </div>
  );
}
